import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import { type SystemStyleObject, type Theme } from '@mui/system';

import { glassSurfaceSx } from '@/theme';

import { PageIntroHighlightItem, type PageIntroHighlight } from './PageIntroHighlightItem';
import { STRIP_RULE } from './pageIntroInk';

/**
 * How the strip is padded from `md` up. Stated once because the glass bar and
 * the plain bar have to agree: they are the same object on two different
 * surfaces, and a strip that changed height between a page with cars and a
 * page without would make the two bands look like two components.
 *
 * Below `md` there is no padding here at all. The claims are chips down there
 * and each one carries its own (PageIntroHighlightItem), so padding on the row
 * as well would be padding twice.
 */
const PADDING = { px: 8, py: 5 };

/**
 * The surfaces, each painted only from `md` up.
 *
 * Nesting the whole recipe under one breakpoint rather than overriding it
 * afterwards, because the two are not the same amount of work: `clear` glass
 * is a fill, a sheen, a 26px backdrop filter, a masked 1px ring, two
 * `@supports` fallbacks and `color: #FFFFFF`, and undoing all of that below
 * `md` means restating every one of them in reverse and keeping the list in
 * step with `theme/glass.ts` forever. Stating when it applies is one line and
 * cannot drift.
 *
 * It also happens to be the honest description of the thing. A `clear` pane
 * frosts the band behind it and needs something *in* that band to refract —
 * below `md` the slideshow is not painted and the surface is flat, which is
 * exactly the case theme/glass.ts warns comes out as a grey slab. The glass is
 * not being hidden on phones; there is no glass on phones, because there is
 * nothing there to be glass over.
 *
 * Cast because `glassSurfaceSx` is typed as a bag of ~1000 optional CSS
 * properties, which `exactOptionalPropertyTypes` will not accept as an `sx`
 * value on its own — the same bargain `theme/patterns.ts` makes with
 * `applyStyles`.
 */
const surfaceFromMd =
  (glass: boolean) =>
  (t: Theme): SystemStyleObject<Theme> =>
    ({
      [t.breakpoints.up('md')]: {
        ...PADDING,
        ...(glass
          ? Object.assign({}, ...glassSurfaceSx('clear'))
          : // `radius.lg` (12px), stated through the theme's 4px shape unit.
            // Not the glass panel's `xl`: that radius exists because a frosted
            // rim needs corner to turn on, and a bordered box does not.
            { border: 1, borderColor: 'divider', borderRadius: 3 }),
      },
    }) as SystemStyleObject<Theme>;

/**
 * The row itself: a rail that scrolls sideways below `md`, the full-width bar
 * it has always been from `md` up.
 *
 * Why a rail and not the column it used to be. Three claims stacked with a
 * 32px gap and 40px of pane padding above and below came to 216px — a quarter
 * of a 390x844 screen, spent on three labels, in the band whose whole problem
 * was height. Sideways they cost 56px and still read: the first two are on
 * screen, the third is visibly cut off at the edge, and the cut edge is what
 * says there is more. That is why the row bleeds past the container's gutter
 * rather than stopping inside it — a scrollable row that ends in 16px of white
 * space looks finished.
 *
 * `scroll-snap` on proximity rather than mandatory: mandatory snapping fights
 * a visitor who only wanted to nudge the row, and these are labels to glance
 * at, not pages to page through.
 *
 * It is focusable, and that is the WCAG 2.1.1 answer rather than an oversight:
 * a region that can only be reached by dragging cannot be reached by keyboard
 * at all unless something in it takes focus, and nothing in it is interactive.
 * The cost is one tab stop from `md` up, where the row does not scroll and the
 * stop buys nothing — the alternative is a JavaScript width check, which this
 * prerendered site cannot make before the HTML is written.
 */
const ROW = {
  listStyle: 'none',
  p: 0,
  m: 0,
  flexWrap: 'nowrap',
  alignItems: { xs: 'stretch', md: 'center' },
  // The rules are flex children like the claims are, so distributing the slack
  // evenly between every child is what centres each rule in the space it
  // divides: the gap before a rule and the gap after it are the same gap,
  // computed once. It also pins the first claim to the left edge and the last
  // to the right, which is what stops a strip of short labels from drifting
  // into the middle of a full-width bar.
  //
  // `md` and up only. In the rail there is no slack to distribute — the row is
  // wider than the screen, which is the point.
  justifyContent: { md: 'space-between' },
  overflowX: { xs: 'auto', md: 'visible' },
  scrollSnapType: { xs: 'x proximity', md: 'none' },
  // Edge to edge, against the container's own gutter (16px, 24px from `sm`),
  // with the padding put back inside so the first chip still lines up under
  // the headline.
  mx: { xs: -4, sm: -6, md: 0 },
  px: { xs: 4, sm: 6, md: 0 },
  // The scrollbar is chrome on a row 56px tall; the cut-off chip is the
  // affordance.
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
};

type Props = {
  items: readonly PageIntroHighlight[];
  /** The band is wearing its stage; see the note on the surfaces above. */
  stage: boolean;
};

/**
 * The proof points: a rail across the foot of the opening band.
 *
 * They used to be a tall pane floating at the band's lower right, which put
 * three one-line labels inside a 380px rectangle and left it reading as an
 * empty slab parked on the car. Across the foot they do the job that research
 * on dealership pages keeps pointing at — trust signals, compact, above the
 * fold, not competing with the headline — and they give the band a floor,
 * which is what the composition was missing when everything sat top-left and
 * the lower right was dead space.
 *
 * A `ul`, because it is a set of unrelated claims and not a sentence broken
 * into pieces. Each row stays quieter than the buttons above it: if these
 * out-shout the call to action the hierarchy has inverted.
 *
 * One element at every width, rather than a rail and a strip swapped over.
 * Two would be two lists in the document and a screen reader would read the
 * claims twice — and the page is prerendered once for every width, so neither
 * copy could be left out of the HTML in the first place.
 */
export function PageIntroTrustStrip({ items, stage }: Props) {
  return (
    <Stack
      component="ul"
      direction="row"
      tabIndex={0}
      aria-label="Highlights"
      spacing={{ xs: 3, md: 0 }}
      divider={
        <Divider
          orientation="vertical"
          flexItem
          // Not in the rail. A rule between chips that already have their own
          // border is a border beside a border, and below `md` there is no
          // slack for it to be centred in anyway.
          sx={{
            display: { xs: 'none', md: 'block' },
            // A floor, not the gap. `space-between` supplies the rest and
            // supplies it symmetrically, so this only guarantees the rule is
            // never touching a label on a narrow desktop where there is no
            // slack left to distribute.
            mx: 4,
            borderColor: stage ? STRIP_RULE : 'divider',
          }}
        />
      }
      sx={[ROW, surfaceFromMd(stage)]}
    >
      {items.map((item) => (
        <PageIntroHighlightItem key={item.label} item={item} stage={stage} />
      ))}
    </Stack>
  );
}
